import { useEffect, useState } from 'react';

import Cookies from 'js-cookie';

/** Dispatched on `window` after the cookie wall writes consent cookies. */
export const COOKIE_CONSENT_CHANGE_EVENT = 'eneco:cookieconsentchange';

const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  return Cookies.get(name);
};

export const isQubitCookieAccepted = (): boolean => getCookie('qubitconsent') === 'Accepted';

/** True once the user has accepted or declined cookies (same signal as CookieWall). */
export const hasCookieConsentDecision = (): boolean =>
  Boolean(getCookie('qubitconsent') && getCookie('privacyConsent') && getCookie('privacyConsent_type'));

const notifyOtherTabs = (): void => {
  try {
    window.localStorage.setItem(COOKIE_CONSENT_CHANGE_EVENT, String(Date.now()));
  } catch {
    // Private mode and disabled storage throw; same-tab listeners still get the event.
  }
};

/** Notify listeners in this tab and others that cookie consent cookies changed. */
export const dispatchCookieConsentChange = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(COOKIE_CONSENT_CHANGE_EVENT));
  notifyOtherTabs();
};

const useCookieFlag = (read: () => boolean): boolean => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const sync = () => {
      const ready = read();
      setIsReady(ready);
      return ready;
    };

    if (sync()) return;

    const onStorage = (event: StorageEvent) => {
      if (event.key !== COOKIE_CONSENT_CHANGE_EVENT) return;
      sync();
    };

    window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, sync);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, [read]);

  return isReady;
};

/**
 * Tracks whether marketing/analytics cookies were accepted (`qubitconsent=Accepted`).
 * Same tab: CookieWall's custom event. Other tabs: a localStorage write that fires `storage`.
 */
export const useQubitCookieConsent = (): boolean => useCookieFlag(isQubitCookieAccepted);

/**
 * Tracks whether the user has made a cookie choice (accept or decline).
 * Use this to defer UI like chat/Usabilla until the cookie wall is answered.
 */
export const useCookieConsentDecision = (): boolean => useCookieFlag(hasCookieConsentDecision);
